import type { ReactNode } from 'react';

export type PreviewDevice = 'desktop' | 'wide' | 'laptop' | 'tablet' | 'mobile';

/**
 * The width each preset gives the preview's own viewport, in CSS px; `null` for Desktop, which takes the whole pane.
 * A preset wider than the pane is scaled down to fit it (`DevicePreviewFrame`), so the page inside still lays out at
 * that width: wide 1440 shows the lab's wide layout even on a laptop screen.
 */
export const PREVIEW_DEVICE_WIDTH: Readonly<Record<PreviewDevice, number | null>> = {
  desktop: null,
  wide: 1440,
  laptop: 1100,
  tablet: 820,
  mobile: 375,
};

/** Every preset, widest first: what the Design Constructor offers. */
export const ALL_PREVIEW_DEVICES: readonly PreviewDevice[] = ['desktop', 'wide', 'laptop', 'tablet', 'mobile'];

export interface DevicePreviewToggleProps {
  value: PreviewDevice;
  onChange(next: PreviewDevice): void;
  /** The presets to offer, in order, each by its width. Left out: the plain Desktop and Mobile pair, by name. */
  devices?: readonly PreviewDevice[];
}

interface DeviceInfo {
  name: string;
  ariaLabel: string;
  icon: ReactNode;
}

const ICON_PROPS = {
  'aria-hidden': true,
  width: 14,
  height: 14,
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.4,
  strokeLinejoin: 'round',
  strokeLinecap: 'round',
} as const;

const DEVICES: Record<PreviewDevice, DeviceInfo> = {
  desktop: {
    name: 'Desktop',
    ariaLabel: 'Desktop preview',
    icon: (
      <svg {...ICON_PROPS}>
        <rect x="1.5" y="2.5" width="13" height="9" rx="1" />
        <path d="M6 14h4M8 11.5V14" />
      </svg>
    ),
  },
  wide: {
    name: 'Wide',
    ariaLabel: 'Wide desktop preview',
    icon: (
      <svg {...ICON_PROPS}>
        <rect x="0.75" y="3.5" width="14.5" height="7.5" rx="1" />
        <path d="M6 14h4M8 11V14" />
      </svg>
    ),
  },
  laptop: {
    name: 'Laptop',
    ariaLabel: 'Laptop preview',
    icon: (
      <svg {...ICON_PROPS}>
        <rect x="3" y="3.5" width="10" height="7" rx="0.8" />
        <path d="M1 12.5h14" />
      </svg>
    ),
  },
  tablet: {
    name: 'Tablet',
    ariaLabel: 'Tablet preview',
    icon: (
      <svg {...ICON_PROPS}>
        <rect x="3" y="1.5" width="10" height="13" rx="1.4" />
        <path d="M7 12.5h2" />
      </svg>
    ),
  },
  mobile: {
    name: 'Mobile',
    ariaLabel: 'Mobile preview',
    icon: (
      <svg {...ICON_PROPS}>
        <rect x="4.5" y="1.5" width="7" height="13" rx="1.4" />
        <path d="M7.25 12.5h1.5" />
      </svg>
    ),
  },
};

/** The short label a preset shows among many: its width, or "Fit" for the full pane. */
function compactLabel(device: PreviewDevice): string {
  const width = PREVIEW_DEVICE_WIDTH[device];
  return width === null ? 'Fit' : String(width);
}

/** The tooltip: the preset's name and width, and that a wider one is scaled to fit. */
function titleOf(device: PreviewDevice): string {
  const width = PREVIEW_DEVICE_WIDTH[device];
  if (width === null) return `${DEVICES[device].name} — the whole pane`;
  return `${DEVICES[device].name} — ${width}px wide, scaled down when the pane is narrower`;
}

/**
 * Segment control for the preview's width. Default: Desktop.
 *
 * Only flips a piece of state: the caller sizes the preview (`ClassicPreviewFrame`, `DevicePreviewFrame`). Uses
 * `role="radiogroup"` semantics: each option is `role="radio"` with `aria-checked`. Without `devices` it is the plain
 * Desktop / Mobile pair, a glyph and a name each, as it has always been. With `devices` each preset shows its width so
 * the row stays short, and its name stays in the tooltip and for screen readers.
 */
export function DevicePreviewToggle({ value, onChange, devices }: DevicePreviewToggleProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Preview device width"
      className="inline-flex items-center gap-0.5 rounded border border-neutral-700 bg-neutral-900 p-0.5"
    >
      {devices ? (
        devices.map((device) => (
          <PresetOption key={device} device={device} active={value === device} onClick={() => onChange(device)} />
        ))
      ) : (
        <>
          <DeviceOption
            active={value === 'desktop'}
            onClick={() => onChange('desktop')}
            ariaLabel={DEVICES.desktop.ariaLabel}
            glyph={'▭'}
            label={DEVICES.desktop.name}
          />
          <DeviceOption
            active={value === 'mobile'}
            onClick={() => onChange('mobile')}
            ariaLabel={DEVICES.mobile.ariaLabel}
            glyph={'▢'}
            label={DEVICES.mobile.name}
          />
        </>
      )}
    </div>
  );
}

interface DeviceOptionProps {
  active: boolean;
  onClick(): void;
  ariaLabel: string;
  glyph: string;
  label: string;
}

function DeviceOption({ active, onClick, ariaLabel, glyph, label }: DeviceOptionProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      aria-label={ariaLabel}
      onClick={onClick}
      className={
        'flex items-center gap-1.5 rounded px-2.5 py-1 font-mono text-[11px] transition-colors ' +
        (active ? 'bg-white text-black' : 'text-neutral-400 hover:text-white')
      }
    >
      <span aria-hidden className="text-[13px] leading-none">{glyph}</span>
      <span>{label}</span>
    </button>
  );
}

interface PresetOptionProps {
  device: PreviewDevice;
  active: boolean;
  onClick(): void;
}

function PresetOption({ device, active, onClick }: PresetOptionProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      aria-label={DEVICES[device].ariaLabel}
      title={titleOf(device)}
      onClick={onClick}
      className={
        'flex items-center gap-1 rounded px-2 py-1 font-mono text-[11px] tabular-nums transition-colors ' +
        (active ? 'bg-white text-black' : 'text-neutral-400 hover:text-white')
      }
    >
      <span aria-hidden className="flex leading-none">
        {DEVICES[device].icon}
      </span>
      <span>{compactLabel(device)}</span>
    </button>
  );
}
